/**
 * Browser data router configuration. Extracted from `main.tsx` so the
 * router can be imported from tests and so the route tree is the single
 * source of truth for the app's URL surface.
 *
 * Tree shape (auth-gated at the layout level — chrome never renders for
 * unauthenticated visitors):
 *
 *   /signin     → <UnauthenticatedLayout> → <SignInPage />
 *   /*          → <RequireAuth><AuthenticatedLayout></RequireAuth>
 *                   children: /Patient, /Task, …, * → <NotFoundPage />
 *
 * @agentic connections
 */

import { Suspense } from "react";
import { Navigate } from "react-router";
// Use Datadog's `createBrowserRouter` wrapper (React RUM plugin, `router: true`)
// so each rendered route produces an accurate RUM view. Same signature as
// `react-router`'s; only the import source changes.
import { createBrowserRouter } from "@datadog/browser-rum-react/react-router-v7";
import { AuthenticatedLayout } from "@/app/routes/AuthenticatedLayout";
import { UnauthenticatedLayout } from "@/app/routes/UnauthenticatedLayout";
import { RequireAuth } from "@/app/auth/RequireAuth";
import { RequireLibraryAccess } from "@/app/routes/RequireLibraryAccess";
import { RouteErrorBoundary } from "@/app/routes/RouteErrorBoundary";
import { PatientPageSkeleton } from "@/components/common/loading/PatientPageSkeleton";
import {
  PageContentSkeleton,
  ResourceDetailSkeleton,
  ResourceListSkeleton,
  TimelineSkeleton,
} from "@benchmark/ui/components/atomic";
import {
  AppointmentsPage,
  HealthcareServiceDetailPage,
  HealthcareServicesPage,
  InsurancePlansPage,
  LibrariesPage,
  LibraryDetailPage,
  LibraryFormPage,
  NotFoundPage,
  OrganizationDetailPage,
  OrganizationsPage,
  PatientPage,
  PatientResourceDetailPage,
  PatientResourcePage,
  PatientsPage,
  PatientTimelinePage,
  PractitionerRoleDetailPage,
  PractitionerRolesPage,
  SignInPage,
  TasksPage,
  TriageDashboardPage,
} from "@/pages";
import { NetworkBandwidthPage } from "@/pages/admin/NetworkBandwidthPage";
import { RequireBandwidthDiagnosticsAccess } from "@/app/routes/RequireBandwidthDiagnosticsAccess";

/** Nested data-router tree. @agentic connections */
export const router = createBrowserRouter([
  {
    path: "/signin",
    element: <UnauthenticatedLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        // No inner Suspense: the lazy `SignInPage` chunk suspends up to
        // `UnauthenticatedLayout`'s `<Suspense fallback={<BootSplash />}>`
        // so unauthenticated visitors only ever see the chrome-less branded
        // splash — never a content-shaped page skeleton.
        index: true,
        element: <SignInPage />,
      },
    ],
  },
  {
    path: "/",
    element: (
      <RequireAuth>
        <AuthenticatedLayout />
      </RequireAuth>
    ),
    errorElement: <RouteErrorBoundary />,
    children: [
      { index: true, element: <Navigate to="/Patient" replace /> },

      {
        path: "Patient",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <PatientsPage />
          </Suspense>
        ),
      },
      {
        path: "Patient/:patientId",
        element: (
          <Suspense fallback={<PatientPageSkeleton />}>
            <PatientPage />
          </Suspense>
        ),
        children: [
          { index: true, element: <Navigate to="timeline" replace /> },
          {
            path: "timeline",
            element: (
              <Suspense fallback={<TimelineSkeleton />}>
                <PatientTimelinePage />
              </Suspense>
            ),
          },
          {
            path: ":resourceType",
            element: (
              <Suspense fallback={<ResourceListSkeleton />}>
                <PatientResourcePage />
              </Suspense>
            ),
          },
          {
            path: ":resourceType/:id",
            element: (
              <Suspense fallback={<ResourceDetailSkeleton />}>
                <PatientResourceDetailPage />
              </Suspense>
            ),
          },
        ],
      },

      {
        path: "Appointment",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <AppointmentsPage />
          </Suspense>
        ),
      },
      {
        path: "HealthcareService",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <HealthcareServicesPage />
          </Suspense>
        ),
      },
      {
        path: "HealthcareService/new",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <HealthcareServicesPage />
          </Suspense>
        ),
      },
      {
        path: "HealthcareService/:id",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <HealthcareServiceDetailPage />
          </Suspense>
        ),
      },
      {
        path: "HealthcareService/:id/edit",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <HealthcareServiceDetailPage />
          </Suspense>
        ),
      },
      {
        path: "PractitionerRole",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <PractitionerRolesPage />
          </Suspense>
        ),
      },
      {
        path: "PractitionerRole/:id",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <PractitionerRoleDetailPage />
          </Suspense>
        ),
      },
      {
        path: "Organization",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <OrganizationsPage />
          </Suspense>
        ),
      },
      {
        path: "Organization/:id",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <OrganizationDetailPage />
          </Suspense>
        ),
      },
      {
        path: "InsurancePlan",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <InsurancePlansPage />
          </Suspense>
        ),
      },
      {
        path: "Library",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <RequireLibraryAccess>
              <LibrariesPage />
            </RequireLibraryAccess>
          </Suspense>
        ),
      },
      {
        path: "Library/new",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <RequireLibraryAccess>
              <LibraryFormPage />
            </RequireLibraryAccess>
          </Suspense>
        ),
      },
      {
        path: "Library/:id",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <RequireLibraryAccess>
              <LibraryDetailPage />
            </RequireLibraryAccess>
          </Suspense>
        ),
      },
      {
        path: "Library/:id/edit",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <RequireLibraryAccess>
              <LibraryFormPage />
            </RequireLibraryAccess>
          </Suspense>
        ),
      },
      {
        path: "ServiceRequest/triage",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <TriageDashboardPage />
          </Suspense>
        ),
      },
      {
        path: "Task",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <TasksPage />
          </Suspense>
        ),
      },
      {
        path: "Task/new",
        element: (
          <Suspense fallback={<ResourceListSkeleton />}>
            <TasksPage />
          </Suspense>
        ),
      },
      {
        path: "Task/:taskId/edit",
        element: (
          <Suspense fallback={<ResourceDetailSkeleton />}>
            <TasksPage />
          </Suspense>
        ),
      },

      {
        path: "admin/network-bandwidth",
        element: (
          <RequireBandwidthDiagnosticsAccess>
            <NetworkBandwidthPage />
          </RequireBandwidthDiagnosticsAccess>
        ),
      },

      {
        path: "*",
        element: (
          <Suspense fallback={<PageContentSkeleton />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
]);
